import React, { useEffect, useState } from "react";
import styled,{keyframes} from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { SG_GET_CHAT_GPT_SUMMARY, CLEAR_CHAT_GPT_SUMMARY } from "constants/actions";

// Example styled components (adjust to your styling system)
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 0;
  font-family: "Raleway", sans-serif;
`;

const Prompt = styled.p`
  font-size: 16px;
  margin-bottom: 10px;
`;

const FeedbackLabel = styled.p`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const FeedbackText = styled.p`
  font-style: italic;
  margin-bottom: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 50px;
  margin-bottom: 1rem;
  padding: 10px;
  borderRadius: 4px,
  border: 1px solid #ddd,
  font-family: "Raleway", sans-serif;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const SmallButton = styled.button`
  padding: 0.5rem 1rem;
  cursor: pointer;
`;

const LoadingMessage = styled.div`
  font-style: italic;
  color: #666;
`;

// Generate a unique token to track this GPT request
const NEW_VALIDATION_TOKEN = uuidv4();

export default function QuestionAiFollowUp({
  question,
  currentAnswer,
  questionList,
  factors,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  showBackButton = false,
  surveyGoal = ""
}) {
  const dispatch = useDispatch();

  const [loadingAI, setLoadingAI] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiFeedback, setAiFeedback] = useState("");
  const [userResponse, setUserResponse] = useState("");
  const [error, setError] = useState("");

  // --- SELECTOR: your Redux store that holds the GPT response
  const gptResponse = useSelector((state) => state.chatgpt?.chat_gpt_summary?.response);



  // 2) Build the prompt you want to send to ChatGPT
  function buildPrompt2() {
    // Adjust instructions to your actual logic
    return `
Your goal is to produce a follow-up question based on the entire conversation so far.
The ultimate context or goal of the survey is: "${surveyGoal}" (if provided).

Now:
1) Generate exactly ONE great follow-up question that logically flows from the last user answer(s).

Do not restate the entire conversation. 
    `.trim();
  }

  // 3) Fetch the AI response on mount
  useEffect(() => {
    if (currentAnswer?.question){
      setAiQuestion(currentAnswer?.question);
      setUserResponse(currentAnswer?.response);
      return;
    }

    setUserResponse("");
    setAiQuestion("");

    const filtered_questionList = questionList.filter(f=>f.type!= "information" && f.type!= "consent")
    const testPrompt = buildPrompt(filtered_questionList, factors, answers, question, []);

    const prompt = buildPrompt2();

    setLoadingAI(true);
    dispatch({ type: CLEAR_CHAT_GPT_SUMMARY }); // Clear old GPT data
    dispatch({
      type: SG_GET_CHAT_GPT_SUMMARY,
      payload: {
        payload: testPrompt,
        token: NEW_VALIDATION_TOKEN,
      },
    });
  }, [dispatch,question.uniqueId]);



  // 4) Once we have the GPT response in Redux, parse out question + feedback
  useEffect(() => {
    if (gptResponse && loadingAI) {
      setLoadingAI(false);
      setAiQuestion(gptResponse);
    }
  }, [gptResponse, loadingAI]);

  useEffect(()=>{
      onAnswerChange(question.uniqueId, {response:userResponse,question:aiQuestion,factor:question.factorTitle});
  },[userResponse,aiQuestion])

  // 5) Handle user input
  function handleResponseChange(e) {
    setUserResponse(e.target.value);

    setError("");
  }

 

  
  return (
    <Wrapper>
      {loadingAI ? (
        <SkeletonLoader/>
      ) : (
        <>
          <Prompt>{aiQuestion}</Prompt>

          <StyledTextarea
            placeholder="Type your response here..."
            value={currentAnswer?.response}
            onChange={handleResponseChange}
          />

          {error && <div style={{ color: "red", marginBottom: "0.5rem" }}>{error}</div>}
        </>
      )}
    </Wrapper>
  );
}



const SkeletonLoader = () => (
  <SkeletonContainer>
    <SkeletonLine width="70%" height="24px" />
    <SkeletonTextArea />
  </SkeletonContainer>
);

// Skeleton Loader Styles
const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 1rem;
  border-radius: 8px;
  width: 100%;
`;

const SkeletonLine = styled.div`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  background-color: #e4e4e7;
  border-radius: 6px;
  margin-bottom: ${props => props.marginBottom || '8px'};
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

const SkeletonTextArea = styled.div`
  width: 100%;
  height: 120px;
  background-color: #e4e4e7;
  border-radius: 6px;
  margin-bottom: 16px;
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;

const SkeletonButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const SkeletonButton = styled.div`
  width: ${props => props.width || '100px'};
  height: 36px;
  background-color: #e4e4e7;
  border-radius: 6px;
  animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`;


const StyledTextarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #2a3039;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  resize: none;
  font-family: 'Raleway', sans-serif;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #2d70e2;
    outline: 0;
  }
`;

/**
 * Builds a prompt for generating a follow-up question.
 * @param {Array} questions - The full survey question list.
 * @param {Array} factors - Array of factor objects.
 * @param {Object} responses - Key-value pairs of { questionId: { response, question } }.
 * @param {Object} currentQuestion - The current question object.
 * @returns {String} - A formatted prompt string.
 */
function buildPrompt(questions, factors, responses, currentQuestion) {
  let prompt = "";

  if (currentQuestion.type === "factorFollowup") {
    // --- Factor Follow-up Logic (Unchanged) ---
    let factorContext = "";
    let factor = findFactorForQuestion(currentQuestion, factors);

    if (factor) {
      const relevantQAs = getRelevantQAsForFactor(factor, questions, responses);
      factorContext = `
Factor: "${factor.title}"
Description: "${factor.description}"
Related Questions & Answers:
${relevantQAs.map(qa => ` - Q: ${qa.question}\n   A: ${qa.answer}`).join("\n")}
      `.trim();
    }

    let followUpContext = "";
    if (factor) {
      const previousFollowUps = gatherPreviousFactorFollowups(currentQuestion, questions, responses);
      if (previousFollowUps.length > 0) {
        followUpContext = `
The user already answered ${previousFollowUps.length} prior follow-up(s) about this factor:
${previousFollowUps.map((fu, i) => `(#${i + 1}) Question: ${fu.question}\n     User Answer: ${fu.response}`).join("\n")}
        `.trim();
      }
    }

    const factorInstructions = `
INSTRUCTIONS:
- You are generating a follow-up question to understand the user's deeper reasoning/feelings, based on the user’s answers so far.
- You must not reference the term "factor" directly, as the user doesn't see that terminology.
- Use only relevant info from the user's existing answers if you need context.
- Pose a single open-ended question; keep it concise, respectful, and direct.
- Avoid being vague or repetitive.
- If multiple topics were asked, ensure the user knows which topic your follow-up references, by referencing the relevant previous question(s).
    `.trim();

    prompt = `
--- CURRENT QUESTION CONTEXT ---
${factorContext}

${followUpContext}

${factorInstructions}
    `.trim();

  } else if (currentQuestion.type === "aiFollowup") {
    // --- AI Agent Follow-up Logic ---
    const aiOptions = currentQuestion.properties || {};
    const agentContext = aiOptions.agentContext || "No context provided.";
    const agentGoal = aiOptions.agentGoal || "Understand the user's responses.";

    const conversationHistory = getConversationHistory(currentQuestion, questions, responses);
    const historyText = conversationHistory.length > 0
      ? conversationHistory.map((item, index) => `
Question ${index + 1}: ${item.question}
Response: ${item.response}
      `).join("\n")
      : "No previous questions or responses available.";

    const aiInstructions = `
INSTRUCTIONS:
- Generate exactly ONE great follow-up question that logically flows from the entire conversation so far.
- Aim to understand the rationale behind the responses given.
- Keep the question concise, respectful, and open-ended.
- Do not restate the entire conversation.
    `.trim();

    prompt = `
--- SURVEY CONTEXT ---
Context: ${agentContext}
Goal: ${agentGoal}

--- CONVERSATION HISTORY ---
${historyText}

${aiInstructions}
    `.trim();
  }

  return prompt;
}

/**
 * Helper function: find the factor that a 'factorFollowup' question belongs to.
 */
function findFactorForQuestion(currentQuestion, factorsList) {
  if (currentQuestion.type !== "factorFollowup") return null;
  if (!currentQuestion.skipConditions || currentQuestion.skipConditions.length === 0) return null;

  // Find the factorId in skipConditions
  const factorId = currentQuestion.skipConditions[0].factorId;
  if (!factorId) return null;

  // Return the matching factor object
  return factorsList.find(f => f.factorId === factorId) || null;
}

/**
 * Helper function: get Q & A pairs for the standard (non-follow-up) questions
 * that contributed to the factor’s score. 
 */
function getRelevantQAsForFactor(factor, allQuestions, responses) {
  const relevant = [];
  
  factor.questionIds.forEach(qId => {
    const questionObj = allQuestions.find(q => q.uniqueId === qId);
    if (!questionObj) return;
    
    // This assumes your responses object for standard questions 
    // is under responses[qId].response (like "item-2": { response: 10 })
    const ans = responses[qId]?.response ?? "No response";
    const qText = questionObj.question || questionObj.properties?.question || qId;

    relevant.push({ question: qText, answer: ans });
  });

  return relevant;
}

/**
 * Gathers previous factor follow-ups for the same factor,
 * but only from questions positioned *before* the current question
 * in the `questions` array.
 */
function gatherPreviousFactorFollowups(currentQuestion, questions, responses) {
  // If not factorFollowup, no previous factor follow-ups
  if (currentQuestion.type !== "factorFollowup") return [];

  // Identify the factorId from the current question
  const factorId = currentQuestion.skipConditions?.[0]?.factorId;
  if (!factorId) return [];

  // Find the index of the current question in the questions array
  const currentIndex = questions.findIndex(q => q.uniqueId === currentQuestion.uniqueId);
  if (currentIndex < 0) return [];

  const previousFUs = [];

  // Loop over all questions *up to* (but not including) currentIndex
  for (let i = 0; i < currentIndex; i++) {
    const q = questions[i];
    const skipConds = q.skipConditions || [];

    // Check if this is a factorFollowup for the same factor
    const isSameFactorFollowup = (
      q.type === "factorFollowup" &&
      skipConds.some(cond => cond.factorId === factorId)
    );

    if (isSameFactorFollowup) {
      // If user answered this follow-up in `responses`
      const answered = responses[q.uniqueId];
      if (answered && answered.response !== undefined) {
        const fuQuestion = answered.question || q.question;
        const fuAnswer   = answered.response;

        previousFUs.push({
          question: fuQuestion || "[No question text]",
          response: fuAnswer
        });
      }
    }
  }

  return previousFUs;
}

/**
 * Gathers the conversation history up to the current question.
 * @param {Object} currentQuestion - The current question object.
 * @param {Array} questionList - The full list of survey questions.
 * @param {Object} responses - Key-value pairs of { questionId: { response, question } }.
 * @returns {Array} - Array of { question, response } objects.
 */
function getConversationHistory(currentQuestion, questionList, responses) {
  const currentIndex = questionList.findIndex(q => q.uniqueId === currentQuestion.uniqueId);
  if (currentIndex < 0) return [];

  const history = [];
  for (let i = 0; i < currentIndex; i++) {
    const q = questionList[i];
    const answer = responses[q.uniqueId];
    if (answer && answer.response !== undefined) {
      const questionText = answer.question || q.question || q.properties?.question || q.uniqueId;
      history.push({ question: questionText, response: answer.response });
    }
  }
  return history;
}